// --------------------------------------------------------------
// Created On: 2025-07-16
// Author: Dimitra Weinstein
//
// Last Modified: 2025-10-27
// Modified By: Zachary Thomas
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------
import React from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "react-bootstrap";
import IconTooltip from "src/components/IconTooltip/IconTooltip";
import styles from "./SubscriptionModal.module.scss";
// import formatDateLocal from "src/utilities/time/formatDateLocal";

// A modal that displays subscription information for a device, including type, status, number, start date, and expiry date.
export default function SubscriptionModal(props: Props): Component {
  const deviceHasSubscription =
    props.subscriptionType !== null && props.subscriptionNumber !== null && props.subscriptionStartDate !== null;
  return (
    <Modal
      show={props.showSubscriptionModal}
      onHide={() => props.onCloseSubscriptionModal()}
      backdropClassName=""
      size="xl"
      centered
      animation
    >
      <ModalHeader>
        <h5 className="font-weight-bold">Subscription</h5>
      </ModalHeader>
      <ModalBody>
        <div className={styles.subscriptionInfo}>
          {props.deviceType} {props.deviceIdentifier}
        </div>
        {deviceHasSubscription ? (
          <table className="table mb-0 pb-0">
            <thead className={styles.thead}>
              <tr>
                <th className={styles.th}>Name</th>
                <th className={styles.th}>Value</th>
                <th className={styles.th}>Info</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Subscription Type</td>
                <td>{props.subscriptionType}</td>
                <td>
                  <IconTooltip
                    id={`subscription-type-row-${props.deviceId}`}
                    icon="info-circle"
                    message={"Name of the subscription type."}
                    color="var(--info-tooltip)"
                  />
                </td>
              </tr>
              <tr>
                <td>Activation Status</td>
                <td>{props.activationStatus}</td>
                <td>
                  <IconTooltip
                    id={`activation-status-row-${props.deviceId}`}
                    icon="info-circle"
                    message={
                      "If the monitoring device supports activation, this will specify if the device is activated, inactive, or deactivated."
                    }
                    color="var(--info-tooltip)"
                  />
                </td>
              </tr>
              <tr>
                <td>Subscription Number</td>
                <td>{props.subscriptionNumber}</td>
                <td>
                  <IconTooltip
                    id={`subscription-number-row-${props.deviceId}`}
                    icon="info-circle"
                    message={"Unique identifier for the subscription."}
                    color="var(--info-tooltip)"
                  />
                </td>
              </tr>
              {/* Commented out <td> element is in place so that when the subscription start and end dates is available, it can be easily uncommented */}
              <tr>
                <td>Start Date</td>
                <td>N/A</td>
                {/* <td>{formatDateLocal(props.subscriptionStartDate)}</td> */}
                <td>
                  <IconTooltip
                    id={`subscription-start-date-row-${props.deviceId}`}
                    icon="info-circle"
                    message={"Date when the subscription starts."}
                    color="var(--info-tooltip)"
                  />
                </td>
              </tr>
              <tr>
                <td>End Date</td>
                <td>N/A</td>
                {/* <td>{formatDateLocal(props.subscriptionExpiry)}</td> */}
                <td>
                  <IconTooltip
                    id={`subscription-expiry-row-${props.deviceId}`}
                    icon="info-circle"
                    message={"Date when the subscription ends."}
                    color="var(--info-tooltip)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        ) : (
          <div className={styles.noStatus}>This device currently has no subscription information.</div>
        )}
      </ModalBody>
      <ModalFooter>
        <button className="btn btn-secondary" type="button" onClick={() => props.onCloseSubscriptionModal()}>
          Close
        </button>
      </ModalFooter>
    </Modal>
  );
}

interface Props {
  deviceId: number;
  deviceType: string;
  deviceIdentifier: string;
  subscriptionType: string;
  activationStatus: string;
  subscriptionNumber: string;
  subscriptionStartDate: string;
  subscriptionExpiry: string;
  showSubscriptionModal: boolean;
  onCloseSubscriptionModal: () => void;
}
